import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Parse File Step
 *
 * Parses file content (CSV, Excel, JSON) into structured rows.
 * Supports headerless files via explicit columnNames, column type
 * coercion, column renaming, and configurable error handling.
 *
 * Example workflow:
 * ```
 * Blob Storage Fetch → Parse File → Pipeline Rows Insert
 * ```
 */
export const parseFileStep = defineStepConfig({
  type: "parse_file",
  name: "Parse File",
  description: "Parse file content (CSV, Excel, JSON) into structured rows",
  category: "Transformation",

  inputSchema: z.object({
    fileBlobKey: z.string().describe("Blob key/path of the file to parse"),
    containerName: z
      .string()
      .optional()
      .describe("Storage container (defaults to system default)"),
    fileName: z
      .string()
      .optional()
      .describe("Original file name (helps auto-detect format)"),
    contentType: z
      .string()
      .optional()
      .describe("MIME type (helps auto-detect format)"),
    storageProfile: z
      .enum(["default", "ship_and_debit"])
      .optional()
      .describe("Storage profile to use (propagated from upstream)"),
  }),

  outputSchema: z.object({
    rowsBlobKey: z
      .string()
      .describe("Scratch blob key containing parsed rows JSON"),
    containerName: z
      .string()
      .describe("Storage container the scratch blob resides in"),
    columns: z.array(z.string()).describe("Detected/configured column names"),
    totalRows: z.number().describe("Total number of rows in the file"),
    skippedRows: z
      .number()
      .describe("Number of rows skipped (empty, parse errors)"),
    parseErrors: z
      .array(
        z.object({
          rowIndex: z.number(),
          error: z.string(),
        }),
      )
      .describe("Per-row parse errors"),
    storageProfile: z
      .enum(["default", "ship_and_debit"])
      .optional()
      .describe("Storage profile used (propagated to downstream steps)"),
    fileName: z
      .string()
      .optional()
      .describe("Original file name (pass-through from input)"),
  }),

  configSchema: z.object({
    format: z
      .enum(["csv", "xlsx", "xls", "json", "fixed_width", "auto"])
      .prefault("auto")
      .describe("File format (auto-detect from extension/content-type)"),
    csvOptions: z
      .object({
        delimiter: z.string().prefault(","),
        quoteChar: z.string().prefault('"'),
        encoding: z.string().prefault("utf-8"),
      })
      .optional()
      .describe("CSV-specific parsing options"),
    fixedWidthOptions: z
      .object({
        columnWidths: z
          .array(z.number())
          .describe(
            "Width of each column in characters, in order. " +
              "Must match the number of columnNames. " +
              "E.g. [5, 2, 1, 30, 30, 25, 25, 6, 3, 1] for VENDR1.",
          ),
      })
      .optional()
      .describe("Fixed-width file parsing options (AS/400 record layouts)"),
    excelOptions: z
      .object({
        sheetName: z
          .string()
          .optional()
          .describe("Sheet name to parse (defaults to first sheet)"),
        sheetIndex: z
          .number()
          .optional()
          .describe("Sheet index, 0-based (alternative to sheetName)"),
      })
      .optional()
      .describe("Excel-specific parsing options"),
    headerRow: z
      .number()
      .prefault(0)
      .describe(
        "Row index (0-based) containing column headers. Set to -1 for no headers.",
      ),
    columnNames: z
      .array(z.string())
      .optional()
      .describe(
        "Explicit column names (required when headerRow=-1 for headerless files)",
      ),
    skipRows: z
      .number()
      .prefault(0)
      .describe("Number of rows to skip before the header row"),
    maxRows: z
      .number()
      .optional()
      .describe("Maximum number of data rows to parse"),
    columnMapping: z
      .record(z.string(), z.string())
      .optional()
      .describe(
        "Map source column names to target field names (e.g., {'Product Name': 'productName'})",
      ),
    columnTypes: z
      .record(
        z.string(),
        z.enum([
          "string",
          "number",
          "integer",
          "float",
          "boolean",
          "date",
          "currency",
          "currency4",
        ]),
      )
      .optional()
      .describe(
        "Type coercion per column (e.g., {'price': 'float', 'quantity': 'integer'})",
      ),
    trimWhitespace: z
      .boolean()
      .prefault(true)
      .describe("Trim whitespace from string values"),
    skipEmptyRows: z
      .boolean()
      .prefault(true)
      .describe("Skip rows where all values are empty"),
    onParseError: z
      .enum(["skip_row", "null_value", "fail"])
      .prefault("skip_row")
      .describe("How to handle per-cell parse/coercion errors"),
    filterRules: z
      .array(
        z.object({
          conditions: z
            .array(
              z.object({
                field: z.string().describe("Field path in row data"),
                operator: z.enum([
                  "eq",
                  "neq",
                  "gt",
                  "gte",
                  "lt",
                  "lte",
                  "contains",
                  "not_contains",
                  "starts_with",
                  "ends_with",
                  "is_null",
                  "is_not_null",
                  "in",
                  "not_in",
                  "regex_match",
                ]),
                value: z.unknown().optional(),
              }),
            )
            .describe("All conditions must match (AND logic)"),
        }),
      )
      .optional()
      .describe(
        "Pre-filter rows BEFORE uploading to scratch storage. " +
          "Same operators as pipeline_rows_insert. Dramatically reduces upload size for large files.",
      ),
    filterMode: z
      .enum(["include", "exclude"])
      .prefault("include")
      .describe(
        "include: only rows matching ANY filter rule are kept. " +
          "exclude: rows matching ANY filter rule are discarded.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    // Parsing streams the source blob from Azure and re-writes NDJSON to a
    // deterministic key, so a fresh attempt is fully idempotent. A transient
    // blob-download abort mid-stream (TLS socket drop) is not retried by the
    // Azure SDK's abort path, so allow the step itself to re-run on failure.
    // Trade-off: with no nonRetryableErrorTypes, permanent failures (missing
    // blob/404, auth error, bad containerName) also burn all 3 attempts — a
    // bounded ~70s delay before the error surfaces. Accepted because malformed
    // rows are skipped via onParseError (not thrown), so step failures are
    // overwhelmingly transient IO that should retry.
    retry: {
      maximumAttempts: 3,
      initialInterval: "10 seconds",
      backoffCoefficient: 2,
      maximumInterval: "1 minute",
    },
  },
});
